import {gql} from '@apollo/client';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  getBenchlingAppForDevConsolePage,
  getBenchlingAppForDevConsolePageVariables,
} from './__generated/getBenchlingAppForDevConsolePage';
import BenchlingAppPageLegacy from './BenchlingAppPageLegacy';
import BenchlingGlobalAppPage from './BenchlingGlobalAppPage';

export const BENCHLING_APP_QUERY = gql`
  query getBenchlingAppForDevConsolePage($installationId: StrictString!, $forDeveloper: StrictBoolean)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $installationId, forDeveloper: $forDeveloper) {
      ... on BenchlingAppNode {
        id
        appDefinitionId
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function BenchlingAppPage({id}: {id: string}): JSX.Element {
  if (id.startsWith('appdef')) {
    return BenchlingConfig.ENABLE_BENCHLING_APPS_GLOBAL_DEFINITIONS ? (
      <BenchlingGlobalAppPage id={id} />
    ) : (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Global apps must be enabled to manage this app."
      />
    );
  }

  return (
    <BenchlingQuery<getBenchlingAppForDevConsolePage, getBenchlingAppForDevConsolePageVariables>
      query={BENCHLING_APP_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{installationId: id, forDeveloper: true}}
    >
      {(data, {refetch}) => {
        if (!isInaccessible(data.benchlingApp)) {
          return BenchlingConfig.ENABLE_BENCHLING_APPS_GLOBAL_DEFINITIONS &&
            data.benchlingApp.appDefinitionId != null ? (
            <BenchlingGlobalAppPage id={data.benchlingApp.appDefinitionId} />
          ) : (
            <BenchlingAppPageLegacy id={id} />
          );
        } else {
          return (
            <div>
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have access to manage this app."
              />
            </div>
          );
        }
      }}
    </BenchlingQuery>
  );
}
